import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useApp } from "../../design/AppContext";
import { apiPostForm } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import {
  IMPORT_COLUMNS,
  MAX_IMPORT_ROWS,
  parseSheet,
  mergeServerResults,
  rowIssues,
  rowWarnings,
  summarize,
  templateHeaders,
  templateSample,
  templateNotes,
} from "../../utils/taskImport";
import { MdDownload, MdUpload } from "react-icons/md";
import { Banner, StatusChip } from "../mui";

/**
 * 用 Excel 匯入工項。
 *
 * ★ 這個對話框已經被整頁版取代：pages/Project/Import.js（/Project/Import/:id，設計稿 #screen-import）。
 *   整頁版把逐列預覽從 13 欄的表格改成逐列清單（大字模式下表格會超寬），是要留下來的那一份。
 *
 *   入口已經切過去了：Project/Detail.js:1061 的「匯入工項」是 navigate("/Project/Import/:id")，
 *   全專案沒有任何一處 <TaskImportDialog />，這支元件現在不會被 render。
 *   本檔依硬規則 8 原樣保留（不刪功能），但唯一還被外面用到的東西是下面的
 *   IMPORT_TIMEOUT_MS —— pages/Project/Import.js:9 從這裡匯入它。要真的清掉這份死碼，
 *   得先把那個常數搬到 utils/taskImport.js（它跟 MAX_IMPORT_ROWS 是一組的），
 *   那是另一件事，不要順手在這裡做。
 *
 *   兩邊打的是同一支端點、用的是同一套 utils/taskImport.js 與同一個 IMPORT_TIMEOUT_MS。
 *
 * 流程刻意是四步而不是「選檔就送出」：
 *   選檔 → 前端解析並逐列預覽（紅字寫明哪一欄為什麼不行）
 *        → 試跑（後端用同一套規則再驗一次，但一個字都不寫）
 *        → 開始匯入（全有全無）
 * 一次要建立幾十上百筆工項，讓使用者在寫入前看得到結果，比事後叫他一筆一筆刪掉合理得多。
 *
 * 對話框用 MUI Dialog：它自己 portal 到 document.body、處理 Esc 與鎖背景捲動，
 * 不會被頁面外層 animation 留下的 transform 困住。
 */

/*
 * SheetJS（xlsx）。
 *
 * 為什麼需要這個依賴：使用者手上的檔案是 .xlsx —— 那是一個 zip 裡面包 XML 的格式，
 * 不是純文字，沒有辦法用 FileReader 加幾行字串處理讀出來。要嘛請使用者先另存成 CSV
 * （等於把「比一個一個開快」省下的時間又還回去，而且 CSV 在 Excel 裡存成 Big5 會整批亂碼），
 * 要嘛把解析放到後端（那就變成「上傳完才知道錯在哪」，沒辦法在送出前逐列預覽）。
 * xlsx 同時吃得下 .xlsx / .xls / .csv，也負責產生「下載範本」的檔案。
 *
 * 為什麼是動態 import 而不是寫在檔頭：這個函式庫壓縮後仍有 140 KB 以上，
 * 靜態 import 會被打進 main bundle，變成「每一個開專案詳情的人」都要為一個
 * 偶爾才用一次的功能多下載一份。改成真的要解析檔案或下載範本時才抓，
 * 平常開專案頁完全不會碰到它。
 *
 * 為什麼 package.json 指的是 https://cdn.sheetjs.com/... 而不是 ^0.18.5：
 * npm registry 上的最後一版就是 0.18.5，而它帶著兩個 high 等級、在 npm 上永遠修不掉的通報 ——
 * GHSA-4r6h-8v6p-xvw6（原型污染，影響 <0.19.3）與 GHSA-5pgg-2g8v-p4x9（ReDoS，影響 <0.20.2）。
 * SheetJS 從 0.19.3 起只發佈在官方 CDN，修好的版本不會出現在 npm 上。
 * 這個相依又不是躺著不動的：底下就是拿使用者選的檔案直接餵 XLSX.read，正是那兩個通報描述的輸入，
 * 一份惡意的 .xlsx 可以在開預覽的人瀏覽器裡污染 Object.prototype 或讓解析卡死。
 * 所以裝的是官方 tarball（package-lock.json 鎖到 0.20.3，npm audit 也就不再列出 xlsx）。
 * 請不要為了「看起來正常一點」把它改回 ^0.18.5。
 */
const loadXlsx = () => import("xlsx");

/**
 * 匯入專用的請求逾時。
 *
 * axios 實例的 timeout 寫死 60 秒（utils/request.js），但一次寫入上百筆工項會跑得比它久。
 * 關鍵是逾時之後：axios 放棄不會停掉伺服器，那個交易很可能照樣 commit ——
 * 於是畫面說「失敗」、資料其實全部進去了，使用者照著訊息重匯一次就是整批重複。
 * 所以這一支單獨放寬到 5 分鐘（列數上限 MAX_IMPORT_ROWS 也一起降到 200 來配合），
 * 真的還是逾時的話，訊息必須講「結果未知」而不是「沒寫入」（見 send 的 catch）。
 *
 * 匯出給整頁版（pages/Project/Import.js）共用：這個值跟 MAX_IMPORT_ROWS 是一組的，
 * 兩個畫面各留一份遲早會走樣，而走樣的後果是「說失敗、其實寫進去了」。
 */
export const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

/** 預覽表格的欄位標題與儲存格（字級走主題的 meta） */
const thSx = { fontSize: "var(--t-meta)", fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" };
const tdSx = {
  fontSize: "var(--t-meta)",
  whiteSpace: "nowrap",
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function TaskImportDialog({ open, projectId, phaseNames, people, issueKeys, onClose, onDone }) {
  const { toast } = useApp();
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  /** 整張表不能用的原因（找不到標題列、缺必填欄、超過列數上限…） */
  const [fatal, setFatal] = useState("");
  const [unknownHeaders, setUnknownHeaders] = useState([]);
  const [busy, setBusy] = useState("");
  /** 後端回來的總計（試跑或匯入） */
  const [result, setResult] = useState(null);
  /**
   * 匯入的請求逾時，結果未知（見 send 的 catch）。
   * 伺服器那邊很可能已經 commit，所以這之後絕對不能再讓人按「開始匯入」——
   * 一按就是第二次完整寫入，最多 200 筆重複工項，而且沒有批次刪除可以收拾。
   */
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setFileName("");
    setRows([]);
    setFatal("");
    setUnknownHeaders([]);
    setResult(null);
    setOutcomeUnknown(false);
    setDragging(false);
    setBusy("");
  }, []);

  /* 每次開啟都清空：上一次匯入完的結果留著，會讓人以為那是這一次的 */
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const readFile = useCallback(
    async (file) => {
      if (!file) return;
      setBusy("parse");
      setResult(null);
      try {
        const XLSX = await loadXlsx();
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        if (!sheet) {
          setRows([]);
          setFatal("此檔案中沒有任何工作表");
          return;
        }
        /*
         * blankrows: true 是必要的 —— 空白列若在這裡就被丟掉，後面每一列的列號都會往前位移，
         * 錯誤訊息講的「第 12 列」就會指到 Excel 上的另一列。空白列的略過交給 parseSheet。
         */
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, blankrows: true, defval: "" });
        /*
         * sheet_to_json 是從工作表 !ref 的起點開始輸出，不是從第 1 列：dimension 若是 A3:L20
         * （使用者在上面留了空列，或檔案是別的系統產的），aoa 的索引 0 其實是 Excel 的第 3 列。
         * 不把這個位移算進去，「第 2 列的標題不可空白」就會指到使用者畫面上的第 4 列，
         * 上面保留 blankrows: true 不讓列號位移的用心也會被這個洞抵銷掉。
         */
        const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;
        const originRowNo = range && Number.isFinite(range.s.r) ? range.s.r + 1 : 1;
        const parsed = parseSheet(aoa, { phaseNames, people, issueKeys }, originRowNo);
        if (parsed.error) {
          setRows([]);
          setFatal(parsed.error);
          setUnknownHeaders([]);
          return;
        }
        setFatal("");
        setRows(parsed.rows);
        setUnknownHeaders(parsed.unknownHeaders || []);
      } catch (e) {
        setRows([]);
        setFatal(`讀取失敗：${e && e.message ? e.message : "檔案格式不正確"}。請確認檔案格式為 .xlsx / .xls / .csv。`);
      } finally {
        setBusy("");
      }
    },
    [phaseNames, people, issueKeys]
  );

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    setFileName(file ? file.name : "");
    readFile(file);
    // 同一個檔案改完再選一次也要能觸發 change
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    setFileName(file.name);
    readFile(file);
  };

  /** 下載範本：第一列欄位標題、第二列一筆填好的範例，另附「欄位說明」工作表 */
  const downloadTemplate = async () => {
    try {
      const XLSX = await loadXlsx();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([templateHeaders(), templateSample()]);
      ws["!cols"] = templateHeaders().map((h) => ({ wch: Math.max(12, h.length * 2 + 6) }));
      XLSX.utils.book_append_sheet(wb, ws, "工項");

      const notes = XLSX.utils.aoa_to_sheet(templateNotes());
      notes["!cols"] = [{ wch: 14 }, { wch: 8 }, { wch: 78 }];
      XLSX.utils.book_append_sheet(wb, notes, "欄位說明");

      XLSX.writeFile(wb, "工項匯入範本.xlsx");
    } catch (e) {
      toast("範本下載失敗，請稍後再試", "error");
    }
  };

  const send = async (dryRun) => {
    if (!rows.length) return;
    setBusy(dryRun ? "dry" : "import");
    try {
      const data = await apiPostForm(
        "/Task/Import",
        {
          Data: jwtEncode({
            ProjectId: Number(projectId),
            DryRun: !!dryRun,
            Rows: rows.map((r) => r.payload),
          }),
        },
        { timeout: IMPORT_TIMEOUT_MS }
      );
      setRows((s) => mergeServerResults(s, data && data.results));
      setResult(data || null);

      if (!dryRun && data && data.committed) {
        toast(data.message || "已匯入工項");
        // 視窗留著給使用者看逐列結果（含新代碼），列表在背景重新整理
        if (onDone) onDone();
      } else if (data && data.message) {
        toast(data.message, data.failed > 0 ? "error" : undefined);
      }
    } catch (e) {
      /*
       * 逾時必須跟一般失敗分開講。axios 放棄之後伺服器那邊還在跑，而且很可能照樣 commit，
       * 所以這時候說「匯入失敗」是一句會害人的話 —— 使用者照著重匯一次就是整批重複。
       * 講「結果未知、先去列表確認」才是他當下真正做得到、也真正該做的事。
       */
      const timedOut = !!e && (e.code === "ECONNABORTED" || /timeout/i.test(String(e.message || "")));
      if (timedOut && !dryRun) {
        // 只靠文字擋不住手比腦快的人：底下的按鈕一起停用，才不會一鍵就是整批重複
        setOutcomeUnknown(true);
        setFatal(
          "匯入請求逾時中斷，但伺服器可能已完成寫入，目前結果未知。" +
            "請先關閉此視窗，重新整理工項列表確認是否已建立，再決定是否重新匯入；直接重新匯入可能導致整批重複。"
        );
        toast("匯入結果未知，請重新整理工項列表確認後，再決定是否重新匯入", "error");
      } else {
        toast(apiErrorMessage(e, dryRun ? "試跑失敗" : "匯入失敗"), "error");
      }
    } finally {
      setBusy("");
    }
  };

  if (!open) return null;

  const sum = summarize(rows);
  const committed = !!(result && result.committed);
  /*
   * 逾時之後（outcomeUnknown）連試跑都停掉：那時候試跑只會回「全部通過驗證，可以開始匯入」，
   * 跟畫面上那段「伺服器可能已經寫入完成」正好相反，等於親手把人推回去按匯入。
   * 這個狀態只有一條出路 —— 關掉視窗、重新整理工項列表確認 —— 所以就只留這條。
   */
  const canDry = rows.length > 0 && !busy && !committed && !outcomeUnknown;
  const canImport = canDry && sum.bad === 0;

  return (
    <Dialog
      open
      onClose={() => {
        // 正在寫入時不讓它關掉（Esc、點遮罩都一樣）：關了也停不下來，只會讓人不知道結果如何
        if (!busy) onClose();
      }}
      maxWidth="lg"
      fullWidth
      aria-labelledby="task-import-title"
      slotProps={{ paper: { sx: { maxHeight: "88vh" } } }}
    >
      {/* ---------- 標題列 ---------- */}
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
      >
        <div>
          <Typography variant="h2" component="h2" id="task-import-title">
            匯入工項
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            使用 Excel 一次建立多筆工項，單次上限 {MAX_IMPORT_ROWS} 列
          </Typography>
        </div>
        <Button variant="text" onClick={() => { if (!busy) onClose(); }}>
          關閉
        </Button>
      </DialogTitle>

      {/* ---------- 內容 ---------- */}
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* 選檔 / 拖放 */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current && fileRef.current.click()}
          role="presentation"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            p: 3,
            border: "1.5px dashed",
            borderColor: dragging ? "primary.main" : "divider",
            borderRadius: "var(--radius-md)",
            bgcolor: dragging ? "primary.light" : "action.hover",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <Box component="span" sx={{ display: "flex", color: "primary.main", fontSize: "var(--icon)" }}>
            <MdUpload />
          </Box>
          <Typography sx={{ fontWeight: 600 }}>{fileName || "將檔案拖曳至此，或點選以選擇檔案"}</Typography>
          <Typography variant="body2" color="text.secondary">
            支援 .xlsx / .xls / .csv（CSV 請以 UTF-8 儲存，否則中文將顯示為亂碼）。
            第一列須為欄位標題，欄位順序可自行調整。
          </Typography>
          <input
            ref={fileRef}
            type="file"
            hidden
            accept=".xlsx,.xls,.csv"
            onChange={onPick}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>

        {/* 整張表不能用 */}
        {!!fatal && <Banner kind="attention">{fatal}</Banner>}

        {/* 統計 + 認不得的欄位 */}
        {rows.length > 0 && (
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              共 {sum.total} 列
            </Typography>
            <StatusChip kind="progress">可匯入 {sum.ok} 筆</StatusChip>
            <StatusChip kind={sum.bad ? "attention" : "closed"}>有問題 {sum.bad} 筆</StatusChip>
            {/* 提醒＝里程碑／指派人／父工項在這一頁的名單裡對不到。匯得進去，只是值得再看一眼 */}
            {!!sum.warn && <StatusChip kind="warn">提醒 {sum.warn} 筆</StatusChip>}
            {!!unknownHeaders.length && (
              <Typography variant="body2" color="text.secondary" component="span">
                已略過無法辨識的欄位：{unknownHeaders.join("、")}
              </Typography>
            )}
          </Stack>
        )}

        {/* 後端回來的總結 */}
        {!!(result && result.message) && (
          <Banner kind={committed ? "done" : "info"}>{result.message}</Banner>
        )}

        {/* 預覽表格 */}
        {rows.length > 0 && (
          <TableContainer sx={{ border: 1, borderColor: "divider", maxHeight: "46vh" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 52 }}>列</TableCell>
                  {IMPORT_COLUMNS.map((c) => (
                    <TableCell key={c.key} sx={thSx}>
                      {c.label}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...thSx, minWidth: 260 }}>檢查結果</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const issues = rowIssues(row);
                  const warns = rowWarnings(row);
                  const bad = issues.length > 0;
                  return (
                    <TableRow
                      key={row.rowNo}
                      sx={bad ? { bgcolor: "error.light" } : warns.length ? { bgcolor: "warning.light" } : undefined}
                    >
                      <TableCell sx={{ ...tdSx, fontVariantNumeric: "tabular-nums", color: "text.secondary", fontWeight: 700 }}>
                        {row.rowNo}
                      </TableCell>
                      {IMPORT_COLUMNS.map((c) => (
                        <TableCell key={c.key} sx={tdSx} title={row.values[c.key] || ""}>
                          {row.values[c.key] || (
                            <Box component="span" sx={{ color: "text.disabled" }}>
                              —
                            </Box>
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ ...tdSx, whiteSpace: "normal", maxWidth: 360 }}>
                        {bad ? (
                          <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                            {issues.join("；")}
                          </Box>
                        ) : row.issueKey ? (
                          <Box component="span" sx={{ color: "success.main", fontWeight: 700 }}>
                            已建立 {row.issueKey}
                          </Box>
                        ) : warns.length ? (
                          /* 提醒用琥珀色而不是紅色：這一列匯得進去，顏色不該讓人以為要先去修 */
                          <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>
                            {warns.join("；")}
                          </Box>
                        ) : (
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            可匯入
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* ---------- 底部操作 ---------- */}
      <DialogActions sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1, px: 3, py: 2 }}>
        <Button variant="outlined" startIcon={<MdDownload />} onClick={downloadTemplate}>
          下載範本
        </Button>

        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 700 }}>
            {committed
              ? "匯入完成"
              : outcomeUnknown
              ? "結果未知，請關閉視窗並重新整理工項列表確認"
              : "請先試跑確認無誤，再開始匯入"}
          </Typography>
          <Button variant="text" onClick={() => { if (!busy) onClose(); }}>
            {committed ? "完成" : "取消"}
          </Button>
          {/* 停用的按鈕不會觸發 Tooltip，外面包一層 span 才看得到原因 */}
          <Tooltip title="送至後端再次驗證，不會寫入任何資料">
            <span>
              <Button variant="outlined" onClick={() => send(true)} disabled={!canDry} loading={busy === "dry"} loadingPosition="start">
                {busy === "dry" ? "試跑中…" : "試跑"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              outcomeUnknown
                ? "上次匯入的結果未知，請先關閉視窗，重新整理工項列表確認是否已建立"
                : sum.bad
                ? "仍有資料列有問題，請先修正檔案再匯入"
                : "開始寫入工項"
            }
          >
            <span>
              <Button variant="contained" onClick={() => send(false)} disabled={!canImport} loading={busy === "import"} loadingPosition="start">
                {busy === "import" ? "匯入中…" : `開始匯入${sum.ok ? ` ${sum.ok} 筆` : ""}`}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

TaskImportDialog.propTypes = {
  open: PropTypes.bool,
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** 本專案的里程碑名稱，用來即時提醒「里程碑」欄打錯字 */
  phaseNames: PropTypes.arrayOf(PropTypes.string),
  /** 可指派的人 [{ name, account }]；空陣列代表前端不檢查，交給後端 */
  people: PropTypes.arrayOf(PropTypes.object),
  /** 本專案既有工項代碼，用來檢查「父工項代碼」欄 */
  issueKeys: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func,
  /** 匯入成功後呼叫（重新整理工項列表） */
  onDone: PropTypes.func,
};

TaskImportDialog.defaultProps = {
  open: false,
  projectId: 0,
  phaseNames: [],
  people: [],
  issueKeys: [],
  onClose: () => {},
  onDone: () => {},
};

export default TaskImportDialog;
